import classNames from 'classnames';

import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {
    CertificationPriceOptions,
    CertificationVoucherUnavailable,
} from '@udemy/react-certification-preparation';
import {VoucherPoolPrice} from '@udemy/react-certification-vouchers';
import {Image} from '@udemy/react-core-components';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {BlockSkeleton} from '@udemy/react-reveal-components';

import {CERT_VOUCHERS_SHOW_ADD_TO_CART} from '../../../../lib/experimentation-constants';
import {trackBuyNowClick} from '../../events';
import styles from './banner.module.less';

const UDEMY_ISSUED_RETAKE_BACKGROUND_SRC =
    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/certification-voucher-landing-page/background-second-chance.webp';

export enum VoucherBulletPointIcon {
    Date = 'today',
    Doc = 'assignment-outline',
}

export type VoucherBulletPoint = {
    icon: VoucherBulletPointIcon;
    text: string;
};

export interface BannerProps {
    voucherId: string;
    isUdemyIssuedRetake?: boolean;
    retakePatternImageUrl?: string;
    logoUrl?: string;
    content: {
        title: string;
        description: string;
        bulletPoints: VoucherBulletPoint[];
    };
    pricing: {
        singlePoolPrice?: VoucherPoolPrice;
        retakeInsurancePrice?: VoucherPoolPrice;
        defaultPrice?: VoucherPoolPrice;
        isLoading: boolean;
    };
    availability: {
        hasStock?: boolean;
        isAvailableForRegion?: boolean;
        hasBothPoolsAvailable?: boolean;
    };
    poolIds: {
        poolId: string;
        retakeInsurancePoolId?: string;
        defaultPoolId?: string;
    };
}

export const Banner = ({
    voucherId,
    isUdemyIssuedRetake = false,
    retakePatternImageUrl,
    logoUrl,
    content,
    pricing,
    availability,
    poolIds,
}: BannerProps) => {
    const {gettext, interpolate} = useI18n();
    const {title, description, bulletPoints} = content;
    const {featureVariantAssignments} = useFeatures([CERT_VOUCHERS_SHOW_ADD_TO_CART]);
    const {
        singlePoolPrice,
        retakeInsurancePrice,
        defaultPrice,
        isLoading: isVoucherPoolPriceLoading,
    } = pricing;
    const {hasStock, isAvailableForRegion = true, hasBothPoolsAvailable} = availability;
    const {poolId, retakeInsurancePoolId, defaultPoolId} = poolIds;

    const onBuyNowClick = () => {
        trackBuyNowClick(
            voucherId, // examId
            poolId, // selected poolId
            title,
            singlePoolPrice?.price_serve_tracking_id,
        );
    };

    const certVouchersShowAddToCart =
        featureVariantAssignments.get(CERT_VOUCHERS_SHOW_ADD_TO_CART)?.configuration
            ?.certVouchersShowAddToCart ?? false;

    return (
        <div className={classNames(styles.background, 'ud-full-viewport-container')}>
            <div className={classNames(styles['banner-container'], 'ud-container')}>
                <div className={classNames(styles.banner)}>
                    <div
                        className={classNames(
                            styles.voucher,
                            isUdemyIssuedRetake && styles['voucher--udemy-issued-retake'],
                        )}
                    >
                        {isUdemyIssuedRetake ? (
                            <div
                                className={styles['voucher-retake-scene']}
                                style={{
                                    backgroundImage: `url(${UDEMY_ISSUED_RETAKE_BACKGROUND_SRC})`,
                                }}
                            >
                                <Image
                                    src={logoUrl}
                                    alt="Voucher"
                                    className={styles['voucher-retake-badge-back']}
                                />
                                <Image
                                    src={logoUrl}
                                    alt="Voucher"
                                    className={styles['voucher-retake-badge-front']}
                                />
                            </div>
                        ) : (
                            <Image src={retakePatternImageUrl} alt="Voucher" />
                        )}
                    </div>
                    <div className={styles.info}>
                        <div className={styles['info-text']}>
                            <div className={styles['main-text']}>
                                <h1 className={'ud-heading-xxl'}>
                                    {isUdemyIssuedRetake
                                        ? interpolate(
                                              gettext(
                                                  '%(title)s Exam Voucher + Udemy Second Chance',
                                              ),
                                              {title},
                                              true,
                                          )
                                        : interpolate(
                                              gettext('%(title)s Exam Voucher'),
                                              {title},
                                              true,
                                          )}
                                </h1>
                                <p className={classNames('ud-text-md', styles.description)}>
                                    {description}
                                </p>
                            </div>

                            <div className={styles.items}>
                                {bulletPoints.map((bulletPoint) => (
                                    <div className={styles.row} key={bulletPoint.text}>
                                        <Icon
                                            glyph={bulletPoint.icon}
                                            label="Default icon"
                                            color="subdued-inverted"
                                        />
                                        <p
                                            className={classNames(
                                                'ud-text-sm',
                                                styles.subdued,
                                                'ud-text-bold',
                                            )}
                                        >
                                            {bulletPoint.text}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {!isAvailableForRegion ? (
                            <div className={styles['info-purchase']}>
                                <CertificationVoucherUnavailable
                                    reason="region"
                                    showMessage={false}
                                    className={styles.unavailable}
                                />
                            </div>
                        ) : hasStock === undefined ? (
                            <div className={styles['info-purchase']}>
                                <BlockSkeleton className={styles['skeleton-loader']} />
                            </div>
                        ) : hasStock ? (
                            <div className={styles['info-purchase']}>
                                <CertificationPriceOptions
                                    examId={voucherId}
                                    pricing={{
                                        retakeInsurance: retakeInsurancePrice,
                                        default: defaultPrice,
                                        single: singlePoolPrice,
                                    }}
                                    isLoading={isVoucherPoolPriceLoading}
                                    poolIds={{
                                        retakeInsurance: retakeInsurancePoolId,
                                        default: defaultPoolId ?? poolId,
                                    }}
                                    checkout={{
                                        onBuyNow: onBuyNowClick,
                                    }}
                                    showBothOptions={hasBothPoolsAvailable}
                                    theme="dark"
                                    showAddToCart={certVouchersShowAddToCart}
                                    showBothCtas={certVouchersShowAddToCart}
                                    priceLayout={
                                        certVouchersShowAddToCart ? 'column' : 'row-reverse'
                                    }
                                    isUdemyIssuedRetake={isUdemyIssuedRetake}
                                />
                            </div>
                        ) : (
                            <div className={styles['info-purchase']}>
                                <CertificationVoucherUnavailable
                                    reason="stock"
                                    className={styles.unavailable}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
